import { DayMessageMap, getChatKey } from '../util/getChatKey';
import { MsgVisibilityOldEnum } from '@pdcplan/shared/enums';
import { dayMessageToMessage, shouldShowAsUnread } from '../Chat/chatUtils';
import { useCallback, useMemo } from 'react';

import { DayMessage } from '@pdcplan/shared/api/Message';
import type { Message } from '@pdcfrontendui/components';

function pushMessage(
  userId: number,
  messages: Message[],
  unreadIds: number[],
  dayMessage: DayMessage
) {
  // Convert the api typing to match the message typing from the component library.
  messages.push(dayMessageToMessage(dayMessage, userId));
  if (shouldShowAsUnread(dayMessage, userId)) {
    unreadIds.push(dayMessage.MessageId);
  }
}

export default function useNotesAndMessages(
  userId: number,
  dayMessageMap: DayMessageMap,
  day: Date,
  personId: number,
  callPostMessage: (
    personId: number,
    messageText: string,
    isInternalMessage: boolean,
    day: Date,
    sentWhen: Date
  ) => void,
  sentWhen: Date
) {
  const dayMessages = useMemo(() => {
    const messages =
      dayMessageMap[getChatKey(personId, day, MsgVisibilityOldEnum.employee)];
    const privateMessages =
      dayMessageMap[getChatKey(personId, day, MsgVisibilityOldEnum.planner)];

    const allMessages = (messages ?? ([] as DayMessage[])).concat(
      privateMessages ?? []
    );

    // Message timestamps only have 1 minute precision, so we sort by id to get the correct order (since ids are ordered)
    return [...allMessages].sort((a, b) => a.MessageId - b.MessageId);
  }, [dayMessageMap, personId, day]);

  const data = useMemo(() => {
    const messages: Message[] = [];
    const internalMessages: Message[] = [];
    // Unread id arrays used for marking as read. So they need to only contain ids that can be mark as read.
    const unreadIds: number[] = [];
    const internalUnreadIds: number[] = [];

    for (const dayMessage of dayMessages) {
      // If Visibility is MsgVisibilityOldEnum.planner, the message is internal
      if (dayMessage.Visibility === MsgVisibilityOldEnum.planner) {
        pushMessage(userId, internalMessages, internalUnreadIds, dayMessage);
      } else {
        pushMessage(userId, messages, unreadIds, dayMessage);
      }
    }

    const hasUnreadMessages = unreadIds.length > 0;
    const hasUnreadInternalMessages = internalUnreadIds.length > 0;

    return {
      messages,
      hasUnreadMessages,
      internalMessages,
      hasUnreadInternalMessages,
    };
  }, [dayMessages, userId]);

  const sendMessage = useCallback(
    (text: string) => {
      callPostMessage(personId, text, false, day, sentWhen);
    },
    [callPostMessage, personId, day, sentWhen]
  );
  const sendInternalMessage = useCallback(
    (text: string) => {
      callPostMessage(personId, text, true, day, sentWhen);
    },
    [callPostMessage, personId, day, sentWhen]
  );

  return {
    messages: data.messages,
    numMessages: data.messages.length,
    hasUnreadMessages: data.hasUnreadMessages,
    sendMessage,

    internalMessages: data.internalMessages,
    numInternalMessages: data.internalMessages.length,
    hasUnreadInternalMessages: data.hasUnreadInternalMessages,
    sendInternalMessage,
  };
}
